/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 이미지 블록을 정의한다.
 *
 * @file /src/rootin/blocks/images/Images.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 28.
 */
import { Block } from '@rootin/base/Block';
import { BlockClass } from '@rootin/base/Blocks';
import { Element } from '@rootin/base/Element';
import { Item } from './blocks/Item';
import { List } from './tools/List';
import { Slide } from './tools/Slide';
import { Card } from './tools/Card';
import { Grid } from './tools/Grid';
import { Ratio } from './tools/Ratio';
import { Paragraph } from '@rootin/blocks/paragraph/Paragraph';
import { Container } from '@rootin/elements/container/Container';
import { Editor } from '@rootin/core/Editor';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';
import { Language, LanguageText } from '@utils/Language';
import './Images.css';

export class Images extends Block {
    /**
     * 블록타입
     */
    static type = 'images';

    /**
     * 블록종류
     */
    static kind: 'inline' | 'atomic' = 'atomic';

    /**
     * 블록아이콘
     */
    static icon =
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-image-icon lucide-image"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';

    /**
     * 블록명
     */
    static label: LanguageText = Language.get('rootin.blocks.images.label');

    /**
     * 슬라이드 이동점 DOM
     */
    $dots: Dom = null;

    /**
     * 슬라이드모드/카드모드에서 이동버튼 DOM
     */
    $move: Map<string, Dom> = new Map();

    /**
     * 이미지 컨테이터
     */
    items: Container;

    /**
     * 이미지 블록을 정의한다.
     *
     * @param editor - 이 블록이 속한 에디터
     */
    constructor(editor: Editor) {
        super(editor);
        this.items = new Container(this, {
            name: 'items',
            blocks: [Item.type],
            insertTarget: false,
            autoSeed: false,
            addHandler: async (container, $marker) => {
                const item = new Item(this.editor);
                await container.insertBlock(item, $marker);

                this.editor.focus.suspend();
                // 이미지 영역만 먼저 잡아보고 싶을 수 있으므로, 바로 업로드 시키진 않는다. await item.image.upload();
            },
            createMarker: (_, isLast) => {
                if (isLast == true) {
                    const $marker = Html.create('div', { 'data-action': 'add' });
                    const $button = Html.create('button', { type: 'button' });
                    $button.append(
                        Html.icon(
                            '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-image-plus-icon lucide-image-plus"><path d="M16 5h6"/><path d="M19 2v6"/><path d="M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/><circle cx="9" cy="9" r="2"/></svg>'
                        )
                    );
                    $button.append(Html.create('span').html(Language.get('rootin.blocks.images.add')));

                    $marker.append($button);
                    return $marker;
                } else {
                    const $marker = Html.create('button', { type: 'button' });

                    $marker.setAttr('data-action', 'add');
                    $marker.setAttr('type', 'button');
                    $marker.append(
                        Html.icon(
                            '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-image-plus-icon lucide-image-plus"><path d="M16 5h6"/><path d="M19 2v6"/><path d="M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/><circle cx="9" cy="9" r="2"/></svg>'
                        )
                    );
                    $marker.on('mousedown', (_, e) => {
                        e.preventDefault();
                    });

                    return $marker;
                }
            },
        });
        this.$getBlock().append(this.items.$getElement());

        /**
         * 이미지모드 및 비율 변경사항을 히스토리와 연동하기 위해 앵커 엘리먼트를 활용한다.
         */
        this.anchor.getValue = () => ({
            mode: this.$getBlock().getAttr('data-mode'),
            ratio: this.$getBlock().getAttr('data-ratio'),
        });
        this.anchor.setValue = async (value: { mode?: string; ratio?: string } | null) => {
            this.$getBlock().setAttr('data-mode', value?.mode ?? 'list');
            this.$getBlock().setAttr('data-ratio', value?.ratio ?? 'origin');
        };
    }

    /**
     * 이미지 목록 모드를 변경한다.
     *
     * @param mode - 지정할 모드
     */
    setMode(mode: string): void {
        this.anchor.onBeforeEdit();
        this.$getBlock().setAttr('data-mode', mode);
        this.anchor.onEdit();
    }

    /**
     * 이미지 비율을 변경한다.
     *
     * @param ratio - 지정할 비율
     */
    setRatio(ratio: string): void {
        this.anchor.onBeforeEdit();
        this.$getBlock().setAttr('data-ratio', ratio);
        this.anchor.onEdit();
    }

    /**
     * 블록을 초기화한다.
     * 하위 블록 클래스가 재정의하여 자신이 사용할 툴을 추가하거나, 기타 초기화작업을 수행한다.
     * 최종적으로 에디터의 블록목록에 추가될 버튼 DOM 을 반환한다.
     *
     * @param editor - 블록이 추가되는 에디터
     * @param self - 하위 블록 클래스
     * @returns 블록추가 버튼 DOM
     */
    static init(editor: Editor): Dom {
        editor.addTool(new List());
        editor.addTool(new Slide());
        editor.addTool(new Card());
        editor.addTool(new Grid());
        editor.addTool(new Ratio());

        return super.init(editor);
    }

    /**
     * 드래그 중인 파일을 블록이 받을 수 있는지 판단한다.
     *
     * @param fileType - 파일의 MIME 타입
     * @returns 판단여부 (insert : 블록추가 및 파일드롭 허용, drop : 파일드롭만 허용, false : 파일드롭을 서용하지 않음)
     */
    static acceptFileDrop(fileType: string): 'insert' | 'drop' | false {
        return fileType.startsWith('image/') ? 'insert' : false;
    }

    /**
     * 슬라이드인 경우, 슬라이드 이동점을 담을 DOM 을 가져온다.
     *
     * @returns 슬라이드 이동점 DOM
     */
    $getDots(): Dom {
        if (this.$dots === null) {
            this.$dots = Html.create('div', { 'data-role': 'dots' });
        }

        return this.$dots;
    }

    /**
     * 슬라이드모드, 카드모드일때 좌우 이동을 할 수 있는 버튼 DOM 을 가져온다.
     *
     * @param direction - 방향
     * @returns 이동버튼 DOM
     */
    $getMove(direction: 'left' | 'right'): Dom {
        if (this.$move.has(direction) == false) {
            const $button = Html.create('button', { type: 'button', 'data-move': direction }, '<i></i>');
            $button.on('mousedown', (_, e) => {
                e.preventDefault();
                e.stopImmediatePropagation();
            });
            $button.on('click', () => {
                const $el = this.items.$getElement().getEl();

                // card 모드는 아이템 하나가 컨테이너 너비(clientWidth)보다 훨씬 좁아서, clientWidth
                // 만큼 스크롤하면 한 번에 여러 개를 건너뛴다(실측 확인) — 아이템 사이 실제 간격을
                // 재서 정확히 한 칸만 이동한다.
                const $items = [...$el.querySelectorAll(`rootin-block[data-type="${Item.type}"]`)];
                let step = $el.clientWidth;
                if ($items.length >= 2) {
                    step = $items[1].getBoundingClientRect().left - $items[0].getBoundingClientRect().left;
                } else if ($items.length === 1) {
                    step = $items[0].getBoundingClientRect().width;
                }

                $el.scrollBy({ left: direction === 'right' ? step : -step, behavior: 'smooth' });
            });

            this.$move.set(direction, $button);
        }

        return this.$move.get(direction);
    }

    /**
     * 타입 문자열로 블록 클래스를 가져온다. 아이템 블록타입은 이 블록만 알고 있으므로 직접
     * 처리하고, 그 외에는 상위 구현에 위임한다.
     *
     * @param type - 블록타입
     * @returns 해당 블록 클래스
     */
    getBlockClass(type: string): BlockClass | null {
        return type === Item.type ? (Item as BlockClass) : super.getBlockClass(type);
    }

    /**
     * 블록을 렌더링한다.
     */
    async render(): Promise<void> {
        await this.items.render();
        this.$getBlock().setAttr('data-mode', 'list');
        this.$getBlock().setAttr('data-ratio', 'origin');

        this.$getBlock().append(this.$getDots());
        this.$getBlock().append(this.$getMove('left'));
        this.$getBlock().append(this.$getMove('right'));

        if (this.items.isEmpty() == true) {
            this.anchor.setAnchorAlways(true);
        }

        this.#syncSlide();
    }

    /**
     * 블록이 비었는지 확인한다.
     *
     * @returns 블록이 비었는지 여부
     */
    isEmpty(): boolean {
        return this.items.isEmpty();
    }

    /**
     * 블록값을 가져온다.
     * element 변수가 존재할 경우, 해당 엘리먼트의 기본값을 가져온다.
     *
     * @param element - 기본값을 가져오려는 엘리먼트 (NULL 인 경우 전체 블록데이터)
     * @returns 블록값
     */
    getValue(element: Element = null): any {
        if (element !== null) {
            return this.defaultValue?.[element.name] ?? null;
        }

        return {
            id: this.getId(),
            type: this.getType(),
            mode: this.$getBlock().getAttr('data-mode'),
            ratio: this.$getBlock().getAttr('data-ratio'),
            items: this.items.getValue(),
        };
    }

    /**
     * 블록값을 지정한다.
     *
     * @param value - 블록값
     * @param is_default_value - 기본값 여부
     */
    async setValue(value: { [key: string]: any }, is_default_value: boolean = false): Promise<void> {
        if (is_default_value === true) {
            this.defaultValue = value;
        }

        this.$getBlock().setAttr('data-mode', value.mode);
        this.$getBlock().setAttr('data-ratio', value.ratio);

        await this.items.setValue(value.items);
        this.#syncSlide();
    }

    /**
     * 블록의 HTML 을 받아, 블록값을 지정한다.
     *
     * @param html - 블록 HTML
     */
    async setValueFromHtml(html: string): Promise<void> {
        const dom = document.createElement('div');
        dom.innerHTML = html;

        const $rootinBlock = dom.firstElementChild;
        if (
            $rootinBlock === null ||
            $rootinBlock.tagName !== 'ROOTIN-BLOCK' ||
            $rootinBlock.getAttribute('data-type') !== this.getType()
        ) {
            return;
        }

        this.$getBlock().setAttr('data-mode', $rootinBlock.getAttribute('data-mode') ?? 'list');
        this.$getBlock().setAttr('data-ratio', $rootinBlock.getAttribute('data-ratio') ?? 'origin');

        const $items = Array.from($rootinBlock.children).find(
            (el) => el.tagName === 'ROOTIN-ELEMENT' && el.getAttribute('data-type') === 'container'
        );
        if ($items !== undefined) {
            await this.items.setValueFromHtml($items);
        }

        this.#syncSlide();
    }

    /**
     * 현재 블록내 선택영역이 존재할 경우, 선택영역을 텍스트로 추출하고 블록에서 제거한다.
     * 이미지는 뽑아낼 텍스트가 없으므로, 선택영역에 닿은 아이템은 항상 통째로 제거한다
     * (`Item.extractSelection()` 참고).
     *
     * @param is_all - 선택영역 부분추출이 아닌 전체를 추출할지 여부
     * @returns 추출한 텍스트 목록과, 선택 이후 남은영역이 포함된 블록(없는 경우 NULL)
     */
    async extractSelection(is_all: boolean): Promise<{ texts: string[]; remain: Block | null }> {
        const range = this.editor.selection.getRange();
        if (range === null || range.collapsed === true) {
            return { texts: [], remain: null };
        }

        // 드래그로 블록 전체가 잠긴(통째로 선택된) 상태라면, 아이템별 판단 없이 전체를 넘긴다.
        const isLocked = this.$getBlock().getAttr('data-selected') === 'true';
        if (isLocked === false && range.intersectsNode(this.$getBlock().getEl()) === false) {
            return { texts: [], remain: null };
        }

        const items = this.items.getBlocks() as Item[];

        if (isLocked === true) {
            for (const item of items) {
                await item.extractSelection(true);
            }

            if (this.isEmpty() === true) {
                await this.getParent()?.removeBlock(this);
            }

            return { texts: [], remain: null };
        }

        // 아이템의 이미지가 선택영역과 실제로 겹치는지로 앞/뒤를 가른다. 겹친 아이템은
        // `item.extractSelection()`이 곧바로 제거하므로, 뒤에 남는(안 겹친) 아이템만 모은다.
        const after: Item[] = [];
        let phase: 'before' | 'after' = 'before';

        for (const item of items) {
            const touched = range.intersectsNode(item.image.$getElement().getEl());
            if (touched === true) {
                phase = 'after';
                await item.extractSelection(true);
            } else if (phase === 'after') {
                after.push(item);
            }
        }

        let remain: Block | null = null;
        if (after.length > 0) {
            const afterImages = this.createRemainBlock();
            for (const item of after) {
                afterImages.items.$getElement().getEl().appendChild(item.$getBlock().getEl());
            }
            remain = afterImages;
        }

        if (this.isEmpty() === true) {
            await this.getParent()?.removeBlock(this);
        }

        return { texts: [], remain };
    }

    /**
     * 선택영역 추출 후 뒤에 남는 아이템들을 담을 새 이미지 블록을 만든다.
     *
     * @returns 새 블록 인스턴스
     */
    private createRemainBlock(): Images {
        return new Images(this.editor);
    }

    /**
     * 블록이 삽입되었을 때 이벤트를 처리한다.
     */
    async onInserted(): Promise<void> {
        const range = this.editor.selection.getRange();
        if (range !== null && range.collapsed === false) {
            const container = this.getParent();
            if (container !== null) {
                let remain: Block | null = null;

                for (const sibling of container.getBlocks()) {
                    if (sibling === this) {
                        continue;
                    }

                    const extracted = await sibling.extractSelection(false);
                    if (extracted.remain !== null) {
                        remain = extracted.remain;
                    }
                }

                if (remain !== null) {
                    await container.insertBlock(remain, this.$getBlock().getEl().nextSibling);
                }
            }
        }

        this.anchor.focus();
    }

    /**
     * 클립보드에서 붙여넣기 하였을 때 이벤트를 처리한다.
     *
     * @param type - 클립보드 타입
     * @param data - 클립보드 데이터
     * @param focused - Element 인 경우 나의 블록의 포커스된 엘리먼트, `Block`인 경우 새 블록을 삽입할 기준블록
     * @returns 처리결과 (true 인 경우 모든 데이터를 처리함, 그렇지 않은 경우 처리되지 않은 데이터)
     */
    async onPaste(type: string, data: any, focused: Block | Element | null = null): Promise<true | any> {
        if (focused === null || focused instanceof Element) {
            return data;
        }

        const container = focused.getParent();
        if (container === null) {
            return data;
        }

        if (type === 'Files') {
            const file = (data as File[])[0] ?? null;
            if (file === null || file.type.startsWith('image/') === false) {
                return data;
            }

            const item = new Item(this.editor);
            this.items.$getElement().append(item.$getBlock());
            await item.render();

            await container.insertBlock(this, focused.$getBlock().getEl().nextSibling);
            await item.onFileDrop(file);

            return true;
        }

        if (type !== 'text/html') {
            return data;
        }

        const dom = document.createElement('div');
        dom.innerHTML = data as string;

        const $matched = dom.querySelector(`rootin-block[data-type='${this.getType()}']`);
        if ($matched !== null) {
            $matched.remove();
            const remain = dom.innerHTML === '' ? true : dom.innerHTML;

            await this.setValueFromHtml($matched.outerHTML);
            await container.insertBlock(this, focused.$getBlock().getEl().nextSibling);

            return remain;
        }

        const $orphans = Array.from(dom.querySelectorAll(`rootin-block[data-type='${Item.type}']`)).filter(
            (el) => (el.parentElement?.closest('rootin-block') ?? null) === null
        );
        if ($orphans.length > 0) {
            for (const $orphan of $orphans) {
                $orphan.remove();
                const item = Item.create(this.editor);
                await item.setValueFromHtml($orphan.outerHTML);
                this.items.$getElement().append(item.$getBlock());
                await item.render();
            }
            const remain = dom.innerHTML === '' ? true : dom.innerHTML;

            await container.insertBlock(this, focused.$getBlock().getEl().nextSibling);

            return remain;
        }

        return data;
    }

    /**
     * 블록이 가진 엘리먼트에서 편집이 일어난 이후 이벤트를 처리한다.
     *
     * @param element - 편집이 일어난 엘리먼트
     */
    onEdit(element: Element): void {
        if (element.name == 'items') {
            this.#syncSlide();
        }

        if (this.items.isEmpty() === true) {
            this.anchor.setAnchorAlways(true);
        } else {
            this.anchor.setAnchorAlways(false);
        }
    }

    /**
     * 블록내에서 Enter 키입력이 발생했을 때 이벤트를 처리한다.
     *
     * @param element - 실제로 Enter 키입력이 발생한 엘리먼트 (NULL 이면 블록자체)
     */
    onEnter(element: Element | null): void {
        if (element === this.anchor) {
            /*
            @todo 구조개선 후 처리
            const $marker = Html.get(
                'rootin-element[data-type=container] > [data-action=add][data-last=true]',
                this.$getBlock()
            );
            // 마커 자신이 버튼이 아니면(래퍼 div) 클릭 핸들러는 그 안의 버튼에서만 반응하므로
            // (Events.ts 참고) 여기서도 버튼을 직접 클릭해야 한다.
            let $button = $marker;
            if ($marker !== null && $marker.is('button') === false) {
                $button = Html.get('button', $marker);
            }
            $button?.getEl().click();
            */
            return;
        }

        this.getParent().insertBlock(new Paragraph(this.editor));
    }

    /**
     * 블록이 포커스를 받았을 때 이벤트를 처리한다.
     *
     * @param element - 캐럿이 위치한 엘리먼트 (NULL 이면 아직 캐럿 위치가 없다는 뜻)
     */
    onFocus(element: Element | null): void {
        if (element === null) {
            this.anchor.focus();
        }
    }

    /**
     * 파일이 드롭되었을 때 이벤트를 처리한다.
     *
     * @param file - 드롭된 파일
     */
    async onFileDrop(file: File): Promise<void> {
        const existingEmpty = Html.all(`rootin-block[data-type="${Item.type}"]`, this.items.$getElement())
            .getList()
            .map(($item) => $item.getData('block') as Item)
            .find((item) => item.image.isEmpty() === true);

        const item = existingEmpty ?? new Item(this.editor);
        if (existingEmpty === undefined) {
            await this.items.insertBlock(item);
        }

        await item.onFileDrop(file);
    }

    /**
     * 슬라이드인 경우 첨부된 이미지 갯수에 따라 슬라이드 이동점을 동기화한다.
     */
    #syncSlide(): void {
        const $dots = this.$getDots();
        $dots.empty();

        const $items = Html.all(`rootin-block[data-type="${Item.type}"]`, this.items.$getElement()).getList();
        for (const $item of $items) {
            const $dot = Html.create('button', { type: 'button' });
            $dot.on('mousedown', (_, e) => {
                e.preventDefault();
            });
            $dot.on('click', () => {
                $item.getEl().scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
            });
            $dots.append($dot);
        }

        if ($items.length > 0) {
            this.anchor.setAnchorAlways(false);
        } else {
            this.anchor.setAnchorAlways(true);
        }
    }
}
